import { Toaster } from "../../ui/toaster";
import { Toaster as Sonner } from "../../ui/sonner";
import { TooltipProvider } from "../../ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Link, Navigate, Route, Routes, useLocation } from "react-router-dom";
import MonitorPage from "./pages/MonitorPage";
import SupersetExpandedPage from "./pages/SupersetExpandedPage";
import CapacityPage from "./pages/CapacityPage";
import SnapshotPage from "./pages/SnapshotPage";
import AttestationPage from "./pages/AttestationPage";
import ManageLimitsPage from "./pages/ManageLimitsPage";
import ManageCapitalPage from "./pages/ManageCapitalPage";
import ManageInvestorsPage from "./pages/ManageInvestorsPage";
import ScenarioSimulatorPage from "./pages/ScenarioSimulatorPage";
import AlertsConfigPage from "./pages/AlertsConfigPage";
import ManageAccess from "./pages/ManageAccess";
import {
  RequireTier,
  hasTier,
  isProductAdmin,
} from "../../components/portfolio-risk-concentration/access-control";
import { RequireDiscountSpreadConfigured } from "../../components/portfolio-risk-concentration/RequireDiscountSpreadConfigured";
import { StepsNavigation, type NavItem } from "../../components/common/StepsNavigation";
import { StepsFooter } from "../../components/common/StepsFooter";
import Header from "../../components/Header";
import RightAside from "../../components/RightAside";
import "../../assets/css/portfolio-risk-concentration/style.css";
import React, { useState, useEffect } from "react";
import { mapUrlToProductId } from "../../utils/common/mapUrlToProduct";
import UserService from "../../services/UserService";
import serverConfig from "../../config/server";
import axios from "axios";

const BASE = "/risk-concentrations";

// Route mode: StepsNavigation/StepsFooter derive the active step from
// location.pathname (via basePath/path) rather than needing step-index state,
// since this feature uses real nested routes, not a wizard-style step machine.
// Built per viewer rather than as a module constant: the three children are role-gated,
// and dropping one here is enough for both chrome components -- StepsNavigation renders
// from this array and StepsFooter derives its prev/next order from the same array, so a
// hidden child also stops the footer walking into it. Step numbering and the a/b child
// lettering recompute from whatever survives.
function navItemsFor(
  canSeeLimits: boolean,
  canSeeAlerts: boolean,
  canSeeCapital: boolean,
  canSeeInvestors: boolean,
  canSeeScenario: boolean,
): NavItem[] {
  const monitorChildren: NavItem[] = [];
  // Manage Capital comes FIRST, ahead of Manage Limits: a capital source has to exist
  // before a limit can be set against it, so setup order and reading order agree. It used
  // to hang off Capacity — the page ABOUT the capital — but that put the two things you
  // configure on different parents.
  if (canSeeCapital) monitorChildren.push({ path: `${BASE}/manage-capital`, label: "Manage Capital" });
  // Straight after Manage Capital, and before limits, for the same dependency reason: Manage
  // Capital maps raw investor codes to a source, and this is what those codes are called. The
  // two are read together.
  if (canSeeInvestors)
    monitorChildren.push({ path: `${BASE}/manage-investors`, label: "Manage Investors" });
  if (canSeeLimits) monitorChildren.push({ path: `${BASE}/manage-limits`, label: "Manage Limits" });
  // "Manage Alerts", not "Alerts", so all three children read as the same kind of thing —
  // a place you go to change something — rather than a verb and a noun. Matches the
  // page's own PageHeader, which already said "Manage Alerts".
  if (canSeeAlerts) monitorChildren.push({ path: `${BASE}/alerts`, label: "Manage Alerts" });

  return [
    {
      path: BASE,
      label: "Monitor",
      ...(monitorChildren.length ? { children: monitorChildren } : {}),
    },
    // Joints used to sit here as a top-level step. It is gone: a joint is not a separate
    // kind of thing to look at, it is another way of expressing a limit, so it now shows
    // up as an ordinary row on Monitor, Capacity and Manage limits -> Approved View, and
    // is created from "+ Define joint" beside "+ Add limit". The reading order is back to
    // "what is my exposure" -> "where do I deploy next" -> "the formatted view".
    //
    // Capacity now has no children either: Manage Capital moved up to Monitor, so the
    // three "manage" pages sit together and Capacity is purely somewhere you read.
    { path: `${BASE}/capacity`, label: "Capacity" },
    // The what-if simulator sits right after Capacity, not appended at the end after
    // Attestation: it's a lens on the same "where do I deploy next" question Capacity
    // answers, just against a hypothetical instead of today's real numbers.
    //
    // A flat entry -- its 5 pages (plus Reconciliation, a 6th, always-enabled entry) show as
    // their own step trail INSIDE ScenarioSimulatorPage.tsx instead (a second, embedded,
    // non-fixed StepsNavigation, in the exact spot ScenarioHeader's old breadcrumb used to
    // sit). They used to be real children of this top-level entry, then Reconciliation was
    // briefly its own top-level sibling here too -- both put wizard sub-steps/status pages in
    // the same flat list as every other page in the product (Monitor's own Manage
    // Capital/Investors/Limits/Alerts children), reading as one long undifferentiated list.
    // Scoped to the wizard instead: see ScenarioSimulatorPage.tsx's SCENARIO_STEPS.
    ...(canSeeScenario ? [{ path: `${BASE}/scenario`, label: "Scenario Portfolio" }] : []),
    { path: `${BASE}/snapshot`, label: "Snapshot" },
    { path: `${BASE}/attestation`, label: "Attestation" },
  ];
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * The full-tab dashboard route.
 *
 * It renders inside this component — so the product access check above still gates it — but
 * WITHOUT the steps navigation, footer and 200px header offset. Expanding is a request for
 * screen space, and reproducing the whole wizard chrome around it would give back most of
 * what the new tab was opened for.
 */
const EXPANDED_PATH = `${BASE}/expanded`;
const MANAGE_ACCESS_PATH = `${BASE}/manage-access`;

const PortfolioRiskConcentration = () => {
  const location = useLocation();
  const isExpanded = location.pathname.startsWith(EXPANDED_PATH);
  const isManageAccess = location.pathname.startsWith(MANAGE_ACCESS_PATH);
  const [accessChecked, setAccessChecked] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  // "Denied" and "could not be determined" are different answers and used to render
  // identically, as "You don't have an active subscription" — so a dropped MySQL tunnel
  // or an expired session read as an entitlement problem and got debugged as one.
  const [accessError, setAccessError] = useState<string | null>(null);

  useEffect(() => {
    const productId = mapUrlToProductId("risk-concentrations");
    UserService.ensureFreshToken()
      .then((jwt: string) =>
        axios.post(`${serverConfig.api_base_url}check-product-access`, {
          jwt,
          product_id: productId,
        })
      )
      .then((res: any) => {
        // The gateway returns 200 with a status field, so a 401/500 body arrives here
        // rather than in .catch — has_access is simply absent on those.
        if (res.data?.has_access === true) {
          setHasAccess(true);
          return;
        }
        setHasAccess(false);
        if (res.data?.status && res.data.status !== 200) {
          setAccessError(res.data.message || `Access check failed (${res.data.status}).`);
        }
      })
      .catch((e: any) => {
        setHasAccess(false);
        setAccessError(
          e?.response?.data?.message ||
            e?.message ||
            "The access check could not reach the platform API."
        );
      })
      .finally(() => {
        setAccessChecked(true);
      });
  }, []);

  // Important: shadcn/Radix components render via portals into <body>.
  // If we scope theme tokens only to an inner wrapper, portal content won't inherit them.
  // So while PortfolioRiskConcentration is mounted, apply a root class + dark mode on <html>.
  React.useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    root.classList.add("portfolio-risk-concentration-root", "dark");
    body.style.backgroundColor = "hsl(218, 30%, 8%)";
    body.style.color = "hsl(30, 9%, 95%)";
    body.style.margin = "0";
    body.style.padding = "0";
    return () => {
      root.classList.remove("portfolio-risk-concentration-root", "dark");
      body.style.backgroundColor = "";
      body.style.color = "";
      body.style.margin = "";
      body.style.padding = "";
    };
  }, []);

  // The gate selects what the product's own routes render -- it deliberately no longer
  // early-returns, because /manage-access has to stay reachable when the gate says no. An
  // account holding only "33-admin" is denied here by design (platform-api's
  // has_any_product_role counts only user/contributor/reviewer as working tiers), and
  // Manage Access is the one place it can grant itself or anyone else a working tier.
  // Same structure, and the same reason, as UnderwritingEngineV2.tsx and RBPE.
  let gatedElement: React.ReactNode = null;
  if (!accessChecked) {
    gatedElement = (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <p className="text-muted-foreground text-sm">Checking access…</p>
      </div>
    );
  } else if (!hasAccess) {
    gatedElement = (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="max-w-md text-center space-y-2 px-6">
          <h2 className="text-lg font-semibold" style={{ color: "#FD9F29" }}>
            {accessError ? "Access check failed" : "Access Denied"}
          </h2>
          <p className="text-sm text-muted-foreground">
            {accessError
              ? accessError
              : "You don't have an active subscription to this product."}
          </p>
          {accessError && (
            <p className="text-xs text-muted-foreground/70">
              This is not an entitlement decision — the check could not complete. Usual
              causes: the MySQL tunnel behind platform-api is down, or your session
              expired.
            </p>
          )}
          {!accessError && isProductAdmin() && (
            <Link
              to={`${BASE}/manage-access`}
              className="inline-block text-sm text-amber hover:underline"
            >
              Manage Access →
            </Link>
          )}
        </div>
      </div>
    );
  }

  // Manage Access renders outside the product chrome and outside the gate. Checked before
  // `isExpanded` and before the denied panel so an admin-only account can actually reach it.
  if (isManageAccess) {
    return (
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <div
            className="portfolio-risk-concentration-theme dark"
            style={{ backgroundColor: "hsl(218, 30%, 8%)", color: "hsl(30, 9%, 95%)" }}
          >
            <Routes>
              <Route path="/manage-access" element={<ManageAccess />} />
            </Routes>
          </div>
        </TooltipProvider>
      </QueryClientProvider>
    );
  }

  if (gatedElement) return gatedElement;

  const navItems = navItemsFor(
    hasTier("reviewer"),
    hasTier("admin"),
    hasTier("reviewer"),
    hasTier("reviewer"),
    hasTier("reviewer"),
  );
  const showManageAccess = isProductAdmin();

  // Chrome-free, and deliberately placed AFTER the access check so the expanded tab is gated
  // exactly like every other route. Same providers and same theme wrapper — only the
  // navigation, footer and header offset are dropped.
  if (isExpanded) {
    return (
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <div
            className="portfolio-risk-concentration-theme dark"
            style={{ backgroundColor: "hsl(218, 30%, 8%)", color: "hsl(30, 9%, 95%)" }}
          >
            <Routes>
              <Route path="/expanded" element={<SupersetExpandedPage />} />
            </Routes>
          </div>
        </TooltipProvider>
      </QueryClientProvider>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <div
          className="portfolio-risk-concentration-theme dark min-h-screen flex flex-col"
          style={{
            backgroundColor: 'hsl(218, 30%, 8%)',
            minHeight: '100vh',
            color: 'hsl(30, 9%, 95%)'
          }}
        >
          <Header
            token={UserService.getToken()}
            isMarketplacePage={false}
            notificationsCount={0}
            refreshNotifications={() => {}}
            setIndustry={() => {}}
            setCompany={() => {}}
            leftNode={null}
            productLabel="Portfolio Risk Concentration"
            fixed={true}
          />
          <StepsNavigation navItems={navItems} basePath={BASE} nextBlocked={false} />

          <main className="flex-1 animate-fade-in pt-[200px] pb-[80px]">
            {/* Same right-aligned link row underwriting-engine and RBPE put at the top of
                their main content. This <main> has no p-6 (page padding comes from
                StepShell/ManageLimitsShell), so the row supplies its own px-6 or it
                renders full-bleed against the viewport edge. */}
            {showManageAccess && (
              <div className="flex justify-end gap-4 mb-3 px-6">
                <Link
                  to={`${BASE}/manage-access`}
                  className="text-xs font-medium text-amber hover:underline"
                >
                  Manage Access →
                </Link>
              </div>
            )}
            <Routes>
              <Route
                path="/"
                element={
                  <RequireDiscountSpreadConfigured>
                    <MonitorPage />
                  </RequireDiscountSpreadConfigured>
                }
              />
              {/* The Joints page is gone, but bookmarks and the ?joint= deep links that
                  Monitor/Capacity used to emit are still out there. Send them to the
                  place joints live now rather than rendering a blank page. */}
              <Route
                path="/joints"
                element={<Navigate to={`${BASE}/manage-limits`} replace />}
              />
              <Route
                path="/capacity"
                element={
                  <RequireDiscountSpreadConfigured>
                    <CapacityPage />
                  </RequireDiscountSpreadConfigured>
                }
              />
              <Route
                path="/scenario/*"
                element={
                  <RequireTier tier="reviewer">
                    <RequireDiscountSpreadConfigured>
                      <ScenarioSimulatorPage />
                    </RequireDiscountSpreadConfigured>
                  </RequireTier>
                }
              />
              <Route
                path="/snapshot"
                element={
                  <RequireDiscountSpreadConfigured>
                    <SnapshotPage />
                  </RequireDiscountSpreadConfigured>
                }
              />
              {/* Gated by route, not just hidden from the nav: a bookmark or a shared
                  link would otherwise walk straight into the page. */}
              <Route
                path="/alerts"
                element={
                  <RequireTier tier="admin">
                    <RequireDiscountSpreadConfigured>
                      <AlertsConfigPage />
                    </RequireDiscountSpreadConfigured>
                  </RequireTier>
                }
              />
              <Route
                path="/attestation"
                element={
                  <RequireDiscountSpreadConfigured>
                    <AttestationPage />
                  </RequireDiscountSpreadConfigured>
                }
              />
              <Route
                path="/manage-limits"
                element={
                  <RequireTier tier="reviewer">
                    <RequireDiscountSpreadConfigured>
                      <ManageLimitsPage />
                    </RequireDiscountSpreadConfigured>
                  </RequireTier>
                }
              />
              {/* Deliberately NOT wrapped in RequireDiscountSpreadConfigured: this is where
                  that setup actually gets finished, so it must always stay reachable. */}
              <Route
                path="/manage-capital"
                element={
                  <RequireTier tier="reviewer">
                    <ManageCapitalPage />
                  </RequireTier>
                }
              />
              <Route
                path="/manage-investors"
                element={
                  <RequireTier tier="reviewer">
                    <RequireDiscountSpreadConfigured>
                      <ManageInvestorsPage />
                    </RequireDiscountSpreadConfigured>
                  </RequireTier>
                }
              />
            </Routes>
          </main>

          <StepsFooter navItems={navItems} basePath={BASE} />
          <RightAside showChildrenWhenNoPanel={false}>{null}</RightAside>
        </div>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default PortfolioRiskConcentration;
